import React from "react";
import Slider from "@mui/material/Slider";

import { DataFormControl } from "../DataFormControl";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { NumberDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/NumberDataDefinition";
import { IssueData } from "../../Utilities/ControlRenderContext";
import { StyledFormHelperText } from "./CommonStyled";
import { PropertyEditorBoolean } from "../../../CommonUI/PropertyEditing/PropertyEditorBoolean";
import { PropertyEditorInteger } from "../../../CommonUI/PropertyEditing/PropertyEditorInteger";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { UISpacedBoxContent } from "../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import { UILabel } from "../../../CommonUI/BuildlingBlocks/UILabel";

class XSliderEditUI extends React.Component<{ editMe: SliderFormControl }> {
    render() {
        return (
            <UISpacedBoxContent>
                {this.props.editMe.renderBaseDataControlEditor()}

                <PropertyEditorBoolean
                    editObject={this.props.editMe}
                    propertyName={"showMinMaxValues"}
                    label="Show Min Max Values"
                />

                <PropertyEditorInteger
                    label="Min"
                    editObject={this.props.editMe}
                    propertyName="min"
                />
                <PropertyEditorInteger
                    label="Max"
                    editObject={this.props.editMe}
                    propertyName="max"
                />
                <PropertyEditorInteger
                    label="Default Value"
                    editObject={this.props.editMe}
                    propertyName="defaultValue"
                    onChange={() => {
                        this.props.editMe.setValueFromDefaultValue();
                    }}
                />
                <PropertyEditorInteger
                    label="Step"
                    editObject={this.props.editMe}
                    propertyName="step"
                />
            </UISpacedBoxContent>
        );
    }
}

export class SliderFormControl extends DataFormControl {
    readonly __type: string = "Slider Control";

    min: number = 0;
    max: number = 10;
    step: number = 1;
    defaultValue?: number = this.min;

    showMinMaxValues: boolean = false;

    constructor() {
        super();
        this.canFocus = false;
    }

    componentDidMount() {
        this.setValueFromDefaultValue();
    }

    setValueFromDefaultValue() {
        // set value as default value at form data. when default value is updated and value is not set.
        if (this.defaultValue !== undefined && this.valueName && !this.getFormDataValue(this.valueName)) {
            this.setFormDataValue(this.valueName, this.defaultValue);
        }
    }

    render() {
        const issueData: IssueData | null = this.getFormContext()!.getControlContext(this)!.getRuntimeIssueData();
        const issueText: string = issueData?.text || "";

        let marks: any[] = [];
        if (this.showMinMaxValues) {
            marks = [
                {
                    value: this.min,
                    label: this.min,
                },
                {
                    value: this.max,
                    label: this.max,
                },
            ];
        }

        return (
            <>
                {this.label && <UILabel variant={"form_label"}>{this.label}</UILabel>}

                <Slider
                    id={this.id}
                    // to fix for Nan warning.
                    defaultValue={this.getValue() || this.min}
                    aria-labelledby="discrete-slider"
                    valueLabelDisplay="auto"
                    step={this.step}
                    min={this.min}
                    max={this.max}
                    onChange={this.handleChange}
                    data-testid={`${this.valueName}`}
                    marks={marks}
                    value={this.getValue()}
                    ref={this.ref}
                />

                {issueText && (
                    <StyledFormHelperText
                        error={Boolean(issueText)}
                        data-testid={`sliderIssueText`}
                    >
                        {issueText}
                    </StyledFormHelperText>
                )}
            </>
        );
    }

    handleChange = (event: any, newValue: number | number[]) => {
        if (this.valueName) {
            this.setFormDataValue(this.valueName, newValue);
        }
    };

    getValue = (): number | undefined => {
        if (this.valueName) {
            let value = this.getFormDataValue(this.valueName) as number;
            if (typeof value === "undefined") {
                return this.defaultValue;
            }
            return value;
        }
        return this.defaultValue;
    };

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        if (!this.valueName) return;

        let def = new NumberDataDefinition();
        def.name = this.valueName;
        return def;
    }

    renderEditUI(): JSX.Element | null {
        return <XSliderEditUI editMe={this} />;
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(
            this,
            retData,
            "__type",
            "min",
            "max",
            "step",
            "defaultValue",
            "showMinMaxValues",
        );

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(this, data, "__type", "min", "max", "step", "defaultValue", "showMinMaxValues");
    }
}
